import React, { useState, useRef, useEffect } from "react";
import DrEditor from "./Editor";
import RemoveButton from "./RemoveButton";
import SequenceableControls from "./SequenceableControls";
import SectionWithAdd from "./SectionWithAdd";
import Spinner from "../../Spinner";
import StatefulRichTextList from "./StatefulRichTextList";

/**
 *
 **/
const StatefulConcepts = (props) => {
  const _isMounted = useRef(false);
  const {
    api,
    containerId,
    editMode,
    focus,
    focusKey,
    lastUpdateTime,
    lastUpdateType,
    reqOptions,
    subjectOpts,
    syllabusId,
  } = props;
  const url = `${api}syl_core_concepts`;
  const [ready, setReady] = useState(false);
  const [concepts, setConcepts] = useState({});
  const [reload, setReload] = useState(true);
  const [show, setShow] = useState(false);
  const transformAndSetConcepts = (data) => {
    if (_isMounted.current) {
      setConcepts(
        data.reduce((h, concept) => {
          h[concept.subject_lookup_id || 0] ||= [];
          h[concept.subject_lookup_id || 0].push(concept);
          return h;
        }, {})
      );
      setShow(data.length > 0);
      setReady(true);
      setReload(false);
    }
  };
  const addItem = (opts, e) => {
    const body = {
      syl_core_concept: {
        syllabus_id: syllabusId,
        sequence: 1,
      },
    };
    apiCall(url, reqOptions("POST", body), (res) =>
      res.json().then((data) => transformAndSetConcepts(data))
    );
  };
  const removeItem = (opts, e) => {
    if (confirm("Are you sure you want to remove this concept?")) {
      const { id } = opts;
      const body = {
        syl_core_concept: { syllabus_id: syllabusId, active: false },
      };
      apiCall(`${url}/${id}`, reqOptions("PATCH", body), (res) =>
        setReload(true)
      );
    }
  };
  const updateItemSequence = (opts, e) => {
    const { id, value } = opts;
    const body = {
      syl_core_concept: { syllabus_id: syllabusId, sequence: value },
    };
    apiCall(`${url}/${id}`, reqOptions("PATCH", body), (res) =>
      setReload(true)
    );
  };

  const subjects = useRef(
    subjectOpts.reduce((h, subject) => {
      h[subject.id] = subject.description;
      return h;
    }, {})
  );
  const conceptKeys = Object.keys(concepts) || [];
  const conceptElems = conceptKeys.map((subjId) => {
    const subjectConcepts = concepts[subjId];
    const subjectName = subjects.current[subjId];
    const subjectHeader = subjectName ? <h3>{subjectName}</h3> : "";
    const maxSequence = subjectConcepts.length;
    return (
      <li key={`subject_concepts_${subjId}`}>
        {subjectHeader}
        <ul className={`sm-font list-unstyled ${subjectName ? "ml-3" : ""}`}>
          {
            subjectConcepts.map((c, ix) => (
              <Concept
                key={`subj-${subjId || 0}-concept-${c.id}`}
                {...props}
                concept={c}
                maxSequence={maxSequence}
                ix={ix}
                removeItem={removeItem}
                updateItemSequence={updateItemSequence}
              />
            )) /**end map() function call**/
          }
        </ul>
      </li>
    );
  });

  /**
   * Initialize and clean up behavior
   * We will load all of the concepts here
   * and map them by subject, but after init,
   * each subject sublist will handle its own
   * concepts.
   **/
  useEffect(function setMounted() {
    _isMounted.current = true;
    return function setUnmounted() {
      _isMounted.current = false;
    };
  }, []);

  /** Reload the syl_core_concepts **/
  useEffect(
    function doReload() {
      if (reload) {
        apiCall(
          `${url}?syl_core_concept[syllabus_id]=${syllabusId}`,
          reqOptions("GET"),
          (res) => res.json().then((data) => transformAndSetConcepts(data))
        ); /** apiCall **/
      } /** if (reload) {....} **/
    },
    [reload]
  );

  /**
   * Process updates from action cable subscription
   **/
  useEffect(
    function recievedUpdate() {
      if (lastUpdateType === "syl_core_concept") setReload(true);
    },
    [lastUpdateTime]
  );

  const display =
    show || editMode ? (
      <SectionWithAdd
        {...props}
        id="syl-core-concepts-section"
        type="syl_core_concept"
        plType="syl_core_concept"
        showProgress={props.canViewProgress}
        addItem={addItem}
      >
        <ul className="list-unstyled top-buffer sm-font">{conceptElems}</ul>
      </SectionWithAdd>
    ) : (
      ""
    );
  return ready ? display : <Spinner />;
};

const Concept = (props) => {
  try {
    const _isMounted = useRef(false);
    const _localUpdate = useRef(false);
    const { api, concept, focusKey, ix, maxSequence, reqOptions, syllabusId } =
      props;
    const url = `${api}syl_core_concepts/${concept.id}`;
    const [description, setDescription] = useState(concept.description);
    const [timer, setTimer] = useState(null);
    const key = `concept-${concept.id}-li`;
    const inFocus = key === focusKey;

    useEffect(function setMounted() {
      _isMounted.current = true;
      return function setUnmounted() {
        _isMounted.current = false;
      };
    }, []);

    useEffect(
      function recievedDescUpdate() {
        _localUpdate.current = false;
        setDescription(props.concept.description);
      },
      [props.concept.description]
    );

    /**
     * If typed into the description box.
     **/
    useEffect(
      function descUpdate() {
        if (_localUpdate.current) {
          const body = {
            syl_core_concept: {
              syllabus_id: syllabusId,
              description: description,
            },
          };
          setTimer(
            debounce(
              () => apiCall(url, reqOptions("PATCH", body), null),
              _isMounted,
              timer,
              3
            )
          );
        }
      },
      [description]
    );

    const changeDescription = (opts, e) => {
      _localUpdate.current = true;
      if (_isMounted.current) setDescription(e.target.value);
    };

    const sendSubjectUpdate = (e) => {
      const body = {
        syl_core_concept: {
          syllabus_id: syllabusId,
          subject_lookup_id: e.target.value,
        },
      };
      apiCall(url, reqOptions("PATCH", body), null);
    };

    // const removeItem = (e) => {
    //   const body = {
    //     syl_core_concept: {
    //       syllabus_id: syllabusId,
    //       active: false
    //     }
    //   };
    //   apiCall(url, reqOptions('PATCH', body), null);
    // };

    return (
      <li
        id={key}
        className="test-syl_core_concept card"
        onClick={(e) => props.focus(key, e)}
      >
        <SubjectDropdown
          {...props}
          inFocus={inFocus}
          handleChange={sendSubjectUpdate}
        />
        <div className="d-flex justify-content-between align-items-top">
          <div className="d-flex justify-content-between w-75 align-items-top">
            <span className="xs-font inner-buffer">{concept.sequence}.</span>
            <DrEditor
              updateType="syl_core_concept"
              defaultText="Core concept..."
              text={description}
              parentKey={key}
              handleChange={changeDescription}
              changeOpts={null}
              focusKey={focusKey}
              dataReady={true}
            />
          </div>
          <SequenceableControls
            {...props}
            item={concept}
            parentKey={key}
            itemIndex={ix}
            handleChange={props.updateItemSequence}
            maxSequence={maxSequence}
            stateKey={concept.subject_lookup_id}
            type="syl_core_concept"
            plType="syl_core_concepts"
          />
        </div>
      </li>
    );
  } catch (e) {
    console.error(e);
  }
};

const SubjectDropdown = (props) => {
  try {
    const { concept, handleChange, subjectOpts, inFocus } = props;
    const subjectOptElems = subjectOpts.map((s) => {
      return (
        <option
          id={`concept-${concept.id}-subject-${s.id}`}
          key={`concept-${concept.id}-subject-${s.id}`}
          value={s.id}
        >
          {s.description}
        </option>
      );
    });

    /** Render Subject Dropdown **/
    return inFocus ? (
      <select
        id={`select-concept-${concept.id}-subject`}
        className="btn btn-light"
        style={{ display: "block" }}
        onChange={(e) => handleChange(e)}
        value={concept.subject_lookup_id || ""}
      >
        <option
          id={`concept-${concept.id}-subject-none`}
          key={`concept-${concept.id}-subject-none`}
          value=""
        >
          -- No Subject --
        </option>
        {subjectOptElems}
      </select>
    ) : (
      ""
    );
  } catch (e) {
    console.error(e);
  }
};

const debounce = (fn, _isMounted, timer, delayFactor) => {
  const delay = (delayFactor || 0) * 100;
  if (timer) clearTimeout(timer);
  if (_isMounted.current) {
    return setTimeout(() => {
      fn();
    }, delay);
  }
};

const apiCall = (url, reqOptions, next) => {
  try {
    fetch(url, reqOptions).then((res) => {
      if (res.status === 401) {
        // alert('Unauthorized. Either your session has expired or you are not authorized to perform this action. Please try logging out and logging in again.')
        // if (confirm('Unauthorized. Either your session has expired or you are not authorized to perform this action. Redirect to login page? \n(Please note: if your session has expired, none of the changes you make to this syllabus will be saved until you log in again.)')) {
        //   sylContainer.props.history.push('/log_in');
        // }
        alert(
          "Not authorized to update the syllabus. Please reload the page and try again."
        );
      } else if (res.status >= 400) {
        alert(
          "There was an issue updating the syllabus. Please reload the page and try again."
        );
      } else if (next) {
        next(res);
      }
    });
  } catch (err) {
    //To Do: handle error
    console.error(err);
  }
};

export default StatefulConcepts;
